import {
  Node,
  NodeMigrationStatusMigrationStatusEnum,
  NodePricing,
  ProblematicNode,
  Workload,
  WorkloadMigrationStatusMigrationStatusEnum,
} from '@cast/types';
import {
  KentRebalancingConstraints,
  KentProblematicNode,
} from '@cast/types-kent';
import { convertUnit } from '@cast/utils';

import { WorkloadBaseRunningInNode, NodeWithDependencies } from '../types';

const getWorkloadsRunningInNodes = (
  workloads?: Workload[]
): WorkloadBaseRunningInNode[] => {
  const workloadsRunningInNode: WorkloadBaseRunningInNode[] = [];

  if (workloads?.length) {
    workloads.reduce((acc, workload) => {
      const requestedResourcesPerReplica: WorkloadBaseRunningInNode['resourcesPerNode']['requested'] =
        {
          milliCpu: 0,
          memoryBytes: 0,
        };

      if (workload.milliCpu && workload.replicas) {
        requestedResourcesPerReplica.milliCpu =
          workload.milliCpu / workload.replicas;
      }

      if (workload.memoryMib && workload.replicas) {
        requestedResourcesPerReplica.memoryBytes = convertUnit(
          workload.memoryMib / workload.replicas,
          'MiB',
          'Bytes'
        );
      }

      workload.nodes?.forEach((node) => {
        const workloadInNode: WorkloadBaseRunningInNode = {
          name: workload.name,
          namespace: workload.namespace,
          workloadType: workload.resource,
          nodeId: node.id,
          nodeName: node.name,
          replicasPerNode: node.workloadReplicas,
          replicasOnAllNodes: workload.replicas,
          resourcesPerNode: {
            requested: {
              milliCpu:
                requestedResourcesPerReplica.milliCpu * node.workloadReplicas,
              memoryBytes:
                requestedResourcesPerReplica.memoryBytes *
                node.workloadReplicas,
            },
          },
          migrationStatus: workload.status!.migrationStatus!,
          issues: workload.issues || [],
        };
        acc.push(workloadInNode);
      });
      return acc;
    }, workloadsRunningInNode);
  }
  return workloadsRunningInNode;
};

const getKentNodeProblems = ({
  node,
  kentRebalancingConstraints,
}: {
  node: Node;
  kentRebalancingConstraints?: KentRebalancingConstraints;
}): KentProblematicNode['problems'] => {
  const problematicNode = kentRebalancingConstraints?.problematicNodes?.find(
    (problematicNode) => problematicNode.nodeName === node.name
  );

  return problematicNode?.problems;
};

type MakeAddNodeDependenciesArgs = {
  nodesPricing?: NodePricing[];
  problematicNodes?: ProblematicNode[];
  workloads?: Workload[];
  isKentRebalancingMode: boolean;
  kentRebalancingConstraints?: KentRebalancingConstraints;
};

// TODO (Justas): Bring back tests
// https://castai.atlassian.net/browse/UI-4747
export const makeAddNodeDependencies = ({
  nodesPricing,
  problematicNodes,
  workloads,
  isKentRebalancingMode,
  kentRebalancingConstraints,
}: MakeAddNodeDependenciesArgs) => {
  if (
    !nodesPricing &&
    !workloads &&
    !problematicNodes &&
    !kentRebalancingConstraints
  ) {
    return undefined;
  }

  const workloadsRunningInNodes = workloads
    ? getWorkloadsRunningInNodes(workloads)
    : undefined;

  return (node: Node) => {
    const pricingInfo = nodesPricing?.find((p) => p.id === node.id);
    const problems = isKentRebalancingMode
      ? getKentNodeProblems({ node, kentRebalancingConstraints })
      : problematicNodes?.find((n) => n.nodeId === node.id)?.problems;

    let hasNotReadyNodes = false;
    const nodeWorkloads: WorkloadBaseRunningInNode[] | undefined =
      workloadsRunningInNodes?.filter((workload) => {
        const isWorkloadOfNode =
          workload.nodeId === node.id || workload.nodeName === node.name;
        if (isWorkloadOfNode && !hasNotReadyNodes) {
          hasNotReadyNodes =
            workload.migrationStatus ===
            WorkloadMigrationStatusMigrationStatusEnum.not_ready;
        }
        return isWorkloadOfNode;
      });

    return {
      ...node,
      pricingInfo,
      problems,
      workloads: nodeWorkloads,
      migrationStatus: nodeWorkloads
        ? hasNotReadyNodes
          ? NodeMigrationStatusMigrationStatusEnum.not_ready
          : NodeMigrationStatusMigrationStatusEnum.ready
        : undefined,
    } as NodeWithDependencies;
  };
};
